
import { Auth } from "@supabase/auth-ui-react";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";
import { getAuthTheme } from "./auth/AuthThemeConfig";
import { useTheme } from "@/components/ui/theme-provider";

interface AuthSectionProps {
  disableRedirect?: boolean;
  freeSignup?: boolean;
}

export function AuthSection({ disableRedirect = false, freeSignup = false }: AuthSectionProps) {
  const session = useSession();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [authTheme, setAuthTheme] = useState(getAuthTheme());

  // Update auth theme when app theme changes
  useEffect(() => {
    const updatedTheme = getAuthTheme();
    
    // Apply dark mode specific styling
    if (theme === 'dark') {
      updatedTheme.appearance.variables.default.colors = {
        ...updatedTheme.appearance.variables.default.colors,
        inputBackground: 'hsl(var(--app-surface))',
        inputBorder: 'hsl(var(--app-border))',
        inputText: 'hsl(var(--app-text-primary))',
        brandButtonText: 'hsl(var(--app-accent-foreground))',
        brand: 'hsl(var(--app-accent))',
        brandAccent: 'hsl(var(--app-accent))',
      };
      
      updatedTheme.appearance.style = {
        ...updatedTheme.appearance.style,
        input: {
          ...updatedTheme.appearance.style.input,
          backgroundColor: 'hsl(var(--app-surface))',
          borderColor: 'hsl(var(--app-border))',
          color: 'hsl(var(--app-text-primary))',
        },
        label: {
          ...updatedTheme.appearance.style.label,
          color: 'hsl(var(--app-text-primary))',
        },
        anchor: {
          ...updatedTheme.appearance.style.anchor,
          color: 'hsl(var(--app-text-primary))',
        }
      };
    }
    
    setAuthTheme(updatedTheme);
  }, [theme]);

  useEffect(() => {
    if (session && !disableRedirect) {
      navigate('/premium');
    }
  }, [session, navigate, disableRedirect]);

  const getContainerClasses = () => {
    if (theme === 'dark') {
      return "min-h-screen flex items-center justify-center p-4 bg-app-background transition-colors duration-300";
    }
    return "min-h-screen flex items-center justify-center p-4 transition-colors duration-300";
  };

  const getContainerStyles = () => {
    if (theme === 'dark') {
      return {};
    }
    return {
      background: "linear-gradient(135deg, rgba(135, 206, 235, 0.1) 0%, rgba(70, 130, 180, 0.05) 100%)"
    };
  };

  const getCardClasses = () => {
    if (theme === 'dark') {
      return "w-full max-w-md p-8 bg-app-surface border border-app-border rounded-lg shadow-lg transition-colors duration-300";
    }
    return "w-full max-w-md p-8 bg-white/90 backdrop-blur-sm border border-blue-100 rounded-lg shadow-lg transition-colors duration-300";
  };

  const getTitleColor = () => {
    return theme === 'dark' ? 'hsl(var(--app-text-primary))' : '#1a365d';
  };

  const getDescriptionColor = () => {
    return theme === 'dark' ? 'text-app-text-secondary' : 'text-gray-600';
  };

  return (
    <div className={getContainerClasses()} style={getContainerStyles()}>
      <div className={getCardClasses()}>
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/97cec83c-b4fa-48c3-a12a-a678346efa1f.png" 
            alt="Grace Awakens Logo" 
            className="w-24 h-24 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold mb-3 transition-colors duration-300" 
              style={{ color: getTitleColor() }}>
            Grace Awakens
          </h1>
          <p className={`text-lg font-medium mb-2 transition-colors duration-300`}
             style={{ color: theme === 'dark' ? 'hsl(var(--app-accent))' : '#1EAEDB' }}>
            Your spiritual journey starts here
          </p>
          <p className={`${getDescriptionColor()} transition-colors duration-300`}>
            {freeSignup ? "Join our community and start your spiritual journey" : "Sign in to continue your spiritual journey"}
          </p>
        </div>
        
        <Auth
          supabaseClient={supabase}
          view={freeSignup ? "sign_up" : "sign_in"}
          appearance={authTheme.appearance}
          localization={authTheme.localization}
          providers={["google"]}
          redirectTo={`${window.location.origin}/premium?success=true`}
          queryParams={{
            access_type: 'offline',
            prompt: 'consent'
          }}
        />
      </div>
    </div>
  );
}
